import { Link } from 'react-router-dom';
import { FiCheck, FiClock, FiHelpCircle } from 'react-icons/fi';
import CopyButton from '../components/transaction/CopyButton';
import TxDetailsPageHeader from '../components/transaction/TxDetailsPageHeader';
import SponsoredSection from '../components/SponsoredSection';
import { useSiteConfig } from '../context/SiteConfigContext';
import { useTronTransaction } from '../hooks/useTronTransaction';
import { formatRelativeTime } from '../utils/formatTime';

function formatTronUtc(unixSeconds) {
  if (!unixSeconds) {
    return '—';
  }
  const iso = new Date(unixSeconds * 1000).toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 19)} (UTC)`;
}

function TronAddress({ address }) {
  if (!address) {
    return <span>—</span>;
  }

  return (
    <Link to={`/lookup?q=${encodeURIComponent(address)}`} className="ttx-addr">
      {address}
    </Link>
  );
}

function TronRow({ label, children, hint }) {
  return (
    <div className="ttx-row">
      <div className="ttx-row__label">
        {label}
        {hint ? (
          <span className="ttx-hint" title={hint}>
            <FiHelpCircle aria-hidden />
          </span>
        ) : null}
      </div>
      <div className="ttx-row__value">{children}</div>
    </div>
  );
}

function TronTransactionDetailsView({ hash }) {
  const { config } = useSiteConfig();
  const { data, loading, error } = useTronTransaction(hash);
  const hasSponsored = Boolean(config?.sponsored?.image_url);

  if (loading) {
    return (
      <main className="sections ttx-page">
        <TxDetailsPageHeader />
        <div className="stx-skeleton stx-skeleton-panel" />
        <div className="stx-skeleton stx-skeleton-panel" />
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="sections ttx-page">
        <TxDetailsPageHeader />
        <div className="stx-error">
          <p>{error || 'Transaction not found.'}</p>
          <Link to="/" className="ttx-addr">
            Back to home
          </Link>
        </div>
      </main>
    );
  }

  const { headline, resources, confirmedSRs, detail } = data;
  const tronscanUrl = `https://tronscan.org/#/transaction/${data.hash}`;

  return (
    <main className="sections ttx-page">
      <TxDetailsPageHeader />

      {headline?.from ? (
        <div className="ttx-headline">
          <TronAddress address={headline.from} />
          <CopyButton text={headline.from} className="ttx-copy-inline" />
          <span className="ttx-headline-action">{headline.action || 'sent'}</span>
          {headline.to ? (
            <>
              <TronAddress address={headline.to} />
              <CopyButton text={headline.to} className="ttx-copy-inline" />
            </>
          ) : null}
          {headline.badge?.label ? (
            <span className="ttx-resource-pill">{headline.badge.label}</span>
          ) : null}
        </div>
      ) : null}

      <section className="ttx-card">
        <TronRow label="Hash:">
          <div className="ttx-hash-line">
            <code className="ttx-mono">{data.hash}</code>
            <CopyButton text={data.hash} />
          </div>
        </TronRow>

        <TronRow label="Result:">
          <span className={`ttx-result-badge${data.success ? ' ttx-result-badge--ok' : ''}`}>
            {data.success ? <FiCheck aria-hidden /> : null}
            {data.resultLabel}
          </span>
        </TronRow>

        <TronRow label="Block &amp; Time:" hint="Block height and timestamp">
          <div className="ttx-inline">
            {data.blockNumber != null ? (
              <>
                <Link to={`/tx/${data.blockNumber}`} className="ttx-addr ttx-addr--bold">
                  {data.blockNumber}
                </Link>
                <CopyButton text={String(data.blockNumber)} className="ttx-copy-inline" />
              </>
            ) : (
              <span>—</span>
            )}
            <span className="ttx-muted">{formatRelativeTime(data.blockTime)}</span>
            <span className="ttx-muted ttx-time-full">
              <FiClock aria-hidden />
              {formatTronUtc(data.blockTime)}
            </span>
          </div>
        </TronRow>

        <TronRow label="Status:" hint="Confirmation status">
          <div className="ttx-inline">
            <span className="ttx-status-badge">{data.statusLabel}</span>
            <span className="ttx-muted">{data.statusDetail}</span>
          </div>
        </TronRow>

        <TronRow label="Confirmed SRs:" hint="Super Representatives">
          <div className="ttx-sr-line">
            <span className="ttx-sr-count">{confirmedSRs?.count ?? 0}</span>
            {confirmedSRs?.names?.length ? (
              <span className="ttx-sr-names">{confirmedSRs.names.join(', ')}</span>
            ) : (
              <span className="ttx-muted">—</span>
            )}
          </div>
        </TronRow>

        <TronRow label="Resources Consumed &amp; Fee:">
          <div className="ttx-resources">
            {resources?.bandwidth != null ? (
              <span>{Number(resources.bandwidth).toLocaleString()} Bandwidth</span>
            ) : null}
            {resources?.energy != null ? (
              <span>{Number(resources.energy).toLocaleString()} Energy</span>
            ) : null}
            {resources?.fee != null ? (
              <span>{(resources.fee / 1e6).toFixed(6)} TRX fee</span>
            ) : null}
            {!resources?.bandwidth && !resources?.energy && !resources?.fee ? (
              <span className="ttx-muted">—</span>
            ) : null}
          </div>
        </TronRow>

        {hasSponsored ? (
          <TronRow label="Sponsored:">
            <SponsoredSection compact showLabel={false} className="sponsored-section--ttx-row" />
          </TronRow>
        ) : null}
      </section>

      {detail?.length > 0 || data.signatureAddresses?.length ? (
        <section className="ttx-card">
          {detail?.map((row) => (
            <TronRow key={row.label} label={`${row.label}:`}>
              {row.kind === 'address' ? (
                <div className="ttx-addr-line">
                  <TronAddress address={row.value} />
                  <CopyButton text={row.value} className="ttx-copy-inline" />
                </div>
              ) : row.kind === 'trx' ? (
                <span className="ttx-trx-value">
                  <span className="ttx-trx-icon" aria-hidden />
                  {row.value}
                  <strong> TRX</strong>
                </span>
              ) : (
                <span>{row.value}</span>
              )}
            </TronRow>
          ))}

          <TronRow label="Signature List:">
            <div className="ttx-sig-list">
              {(data.signatureAddresses || []).map((addr) => (
                <div key={addr} className="ttx-addr-line">
                  <TronAddress address={addr} />
                  <CopyButton text={addr} className="ttx-copy-inline" />
                </div>
              ))}
            </div>
          </TronRow>
        </section>
      ) : null}

      <p className="ttx-external">
        <a href={tronscanUrl} target="_blank" rel="noreferrer" className="ttx-addr">
          View on Tronscan
        </a>
      </p>
    </main>
  );
}

export default TronTransactionDetailsView;
